// hooks/useOptimizerActions.ts
/**
 * Hook for optimizer suggestion actions.
 *
 * Wraps the optimizer API calls with loading state management
 * and automatic plan refresh after mutations.
 *
 * Uses Server-Sent Events (SSE) for push-based completion
 * notification instead of polling. Falls back to polling
 * if SSE is unavailable.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { typedApi } from '../services/typedApiClient';
import { useToast } from './useToast';
import { dispatchDegreeAuditRefresh } from '../utils/appEvents';
import { getErrorMessage } from '../utils/errorUtils';
import { API_URL } from '../utils/constants';
import { supabase } from '../lib/supabase';
import type { OptimizationStatus } from '../types';
import type { OptimizerMode } from '../types/optimizerModes';

export interface UseOptimizerActionsReturn {
  /** Accept a suggestion — turns it into a pinned course */
  acceptSuggestion: (courseCode: string, semester: string) => Promise<void>;
  /** Reject a suggestion — logs constraint and triggers recompute */
  rejectSuggestion: (courseCode: string, semester: string) => Promise<void>;
  /** Generate suggestions for the whole plan */
  generateSuggestions: (optimizerMode?: OptimizerMode) => Promise<void>;
  /** Clear all suggestions from the plan */
  clearSuggestions: () => Promise<void>;
  /** Reassign which gen-ed area an OR gen-ed course satisfies */
  reassignGenEdArea: (courseCode: string, newArea: string) => Promise<void>;
  /** Course code currently being accepted (null if none) */
  acceptingCourse: string | null;
  /** Course code currently being rejected (null if none) */
  rejectingCourse: string | null;
  /** Whether suggestions are currently being generated */
  isGenerating: boolean;
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60000;
const SSE_TIMEOUT_MS = 90000;

export const useOptimizerActions = (
  planId: number | null,
  refreshPlan: () => Promise<void>,
  _optimizationStatus?: OptimizationStatus | null,
): UseOptimizerActionsReturn => {
  const [acceptingCourse, setAcceptingCourse] = useState<string | null>(null);
  const [rejectingCourse, setRejectingCourse] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const { showSuccess, showError } = useToast();
  const eventSourceRef = useRef<EventSource | null>(null);

  // Subscribe to optimizer completion via SSE, with polling fallback.
  useEffect(() => {
    if (!isGenerating || !planId) return;

    let cancelled = false;

    const handleComplete = async () => {
      if (cancelled) return;
      setIsGenerating(false);
      await refreshPlan();
      dispatchDegreeAuditRefresh(planId);
    };

    const handleFailed = () => {
      if (cancelled) return;
      setIsGenerating(false);
      showError('Suggestion generation failed');
    };

    const handleTimeout = () => {
      if (cancelled) return;
      setIsGenerating(false);
      showError('Suggestion generation timed out — please try again');
    };

    // Try SSE first
    const trySSE = async (): Promise<boolean> => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) return false;

        const url = `${API_URL}/api/plans/${planId}/optimizer/events?token=${encodeURIComponent(token)}`;
        const es = new EventSource(url);
        eventSourceRef.current = es;

        const sseTimeout = setTimeout(() => {
          es.close();
          handleTimeout();
        }, SSE_TIMEOUT_MS);

        es.onmessage = async (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.state === 'complete') {
              clearTimeout(sseTimeout);
              es.close();
              await handleComplete();
            } else if (data.state === 'failed') {
              clearTimeout(sseTimeout);
              es.close();
              handleFailed();
            }
            // 'connected', 'timeout' — keep listening
          } catch {
            // Parse error — ignore
          }
        };

        es.onerror = () => {
          // SSE connection failed — fall back to polling
          clearTimeout(sseTimeout);
          es.close();
          eventSourceRef.current = null;
          if (!cancelled) {
            startPolling();
          }
        };

        return true;
      } catch {
        return false;
      }
    };

    // Polling fallback
    const startPolling = () => {
      const timeout = setTimeout(() => {
        clearInterval(poll);
        handleTimeout();
      }, POLL_TIMEOUT_MS);

      const poll = setInterval(async () => {
        try {
          const statusData = await typedApi.optimizer.getStatus(planId);
          const state = statusData?.optimization_status?.state as string | undefined;
          if (state === 'complete') {
            clearInterval(poll);
            clearTimeout(timeout);
            await handleComplete();
          } else if (state === 'failed') {
            clearInterval(poll);
            clearTimeout(timeout);
            handleFailed();
          }
        } catch {
          // Network blip — keep polling
        }
      }, POLL_INTERVAL_MS);

      // Store for cleanup
      cleanupRef.poll = poll;
      cleanupRef.timeout = timeout;
    };

    const cleanupRef: { poll?: ReturnType<typeof setInterval>; timeout?: ReturnType<typeof setTimeout> } = {};

    // Attempt SSE, fall back to polling
    trySSE().then((sseStarted) => {
      if (!sseStarted && !cancelled) {
        startPolling();
      }
    });

    return () => {
      cancelled = true;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (cleanupRef.poll) clearInterval(cleanupRef.poll);
      if (cleanupRef.timeout) clearTimeout(cleanupRef.timeout);
    };
  }, [isGenerating, planId, refreshPlan, showError]);

  const acceptSuggestion = useCallback(async (courseCode: string, semester: string) => {
    if (!planId) return;
    try {
      setAcceptingCourse(courseCode);
      const displayOrder = parseInt(semester, 10);
      await typedApi.optimizer.acceptSuggestion(planId, courseCode, displayOrder);
      showSuccess(`${courseCode} added to your plan`);
      await refreshPlan();
      dispatchDegreeAuditRefresh(planId);
    } catch (err) {
      showError(`Failed to accept suggestion: ${getErrorMessage(err)}`);
    } finally {
      setAcceptingCourse(null);
    }
  }, [planId, refreshPlan, showSuccess, showError]);

  const rejectSuggestion = useCallback(async (courseCode: string, semester: string) => {
    if (!planId) return;
    try {
      setRejectingCourse(courseCode);
      const displayOrder = parseInt(semester, 10);
      await typedApi.optimizer.rejectSuggestion(planId, courseCode, displayOrder);
      showSuccess(`${courseCode} dismissed`);
      await refreshPlan();
    } catch (err) {
      showError(`Failed to dismiss suggestion: ${getErrorMessage(err)}`);
    } finally {
      setRejectingCourse(null);
    }
  }, [planId, refreshPlan, showSuccess, showError]);

  const generateSuggestions = useCallback(async (optimizerMode: OptimizerMode = 'v1') => {
    if (!planId || isGenerating) return;
    try {
      setIsGenerating(true);
      await typedApi.optimizer.optimize(planId, optimizerMode);
      // Initial refresh — SSE/polling takes over for completion detection
      await refreshPlan();
    } catch (err) {
      showError(`Failed to generate suggestions: ${getErrorMessage(err)}`);
      setIsGenerating(false);
    }
    // isGenerating stays true — cleared when SSE/polling detects 'complete'
  }, [planId, isGenerating, refreshPlan, showError]);

  const clearSuggestions = useCallback(async () => {
    if (!planId) return;
    try {
      await typedApi.optimizer.clearSuggestions(planId);
      showSuccess('Suggestions cleared');
      await refreshPlan();
      dispatchDegreeAuditRefresh(planId);
    } catch (err) {
      showError(`Failed to clear suggestions: ${getErrorMessage(err)}`);
    }
  }, [planId, refreshPlan, showSuccess, showError]);

  const reassignGenEdArea = useCallback(async (courseCode: string, newArea: string) => {
    if (!planId) return;
    try {
      await typedApi.optimizer.reassignGenEd(planId, courseCode, { gen_ed_area: newArea });
      showSuccess(`${courseCode} gen-ed area updated to ${newArea}`);
      await refreshPlan();
      dispatchDegreeAuditRefresh(planId);
    } catch (err) {
      showError(`Failed to update gen-ed area: ${getErrorMessage(err)}`);
    }
  }, [planId, refreshPlan, showSuccess, showError]);

  return {
    acceptSuggestion,
    rejectSuggestion,
    generateSuggestions,
    clearSuggestions,
    reassignGenEdArea,
    acceptingCourse,
    rejectingCourse,
    isGenerating,
  };
};
